import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "./api";
import type {
  PaginatedResponse,
  ListResponse,
  PortfolioCurrent,
  HealthResponse,
  FreshnessEntry,
  LangGraphShadowResponse,
  BacktestStrategyGroup,
  SignalDetail,
  SignalReplayResponse,
  PipelineHealthResponse,
  RegimeResponse,
  IntelligenceSummary,
  TargetTrackingEntry,
} from "@midas/shared";
import type {
  CompositeSignal,
  Trade,
  PortfolioSnapshot,
  PriceBar,
  BacktestRun,
  CompanyFundamentals,
  CommodityCompanyMap,
  SectorRotation,
  MacroIndicator,
  MarketEvent,
  CorrelationSnapshot,
  FibTracking,
  SupplyDemand,
  SignalChange,
  SmartMoneyMove,
  CongressTrade,
  GovContract,
  LobbyingActivity,
  DarkPoolActivity,
  AnalystRecommendation,
  EarningsEvent,
  DiscoveryLog,
  CommodityFutures,
  TradeEvaluation,
  TradeEvalStats,
  CommodityAlert,
  AgentPerformance,
  PairsSignal,
  IntelArticle,
  TrackingRequest,
  FuturesCurve,
  CotPosition,
} from "@midas/shared";

// Signals
export function useSignals(params?: Record<string, string>) {
  const search = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["signals", params],
    queryFn: () => api.get<PaginatedResponse<CompositeSignal>>(`/api/signals?${search}`),
  });
}

export function useSignalCounts() {
  return useQuery({
    queryKey: ["signals", "counts"],
    queryFn: () => api.get<{ data: Record<string, number> }>("/api/signals/counts"),
  });
}

export function useSignalDetail(ticker: string) {
  return useQuery({
    queryKey: ["signal", ticker],
    queryFn: () => api.get<SignalDetail>(`/api/signals/${ticker}`),
    enabled: !!ticker,
  });
}

// Portfolio
export function usePortfolioCurrent() {
  return useQuery({
    queryKey: ["portfolio", "current"],
    queryFn: () => api.get<PortfolioCurrent>("/api/portfolio/current"),
    refetchInterval: 60_000,
  });
}

export function usePortfolioSnapshots() {
  return useQuery({
    queryKey: ["portfolio", "snapshots"],
    queryFn: () => api.get<ListResponse<PortfolioSnapshot>>("/api/portfolio/snapshots"),
  });
}

// Trades
export function useTrades(status?: string) {
  const params = status ? `?status=${status}` : "";
  return useQuery({
    queryKey: ["trades", status],
    queryFn: () => api.get<ListResponse<Trade>>(`/api/trades${params}`),
    refetchInterval: 60_000,
  });
}

export function useTradeDecisions(params?: Record<string, string>) {
  const search = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["trades", "decisions", params],
    queryFn: () => api.get<ListResponse<import("@midas/shared").TradeDecision>>(`/api/trades/decisions?${search}`),
    refetchInterval: 60_000,
  });
}

export function useTradeConflicts() {
  return useQuery({
    queryKey: ["trades", "conflicts"],
    queryFn: () => api.get<{ data: import("@midas/shared").TradeConflict[] }>("/api/trades/conflicts"),
  });
}

export function useTrackPerformance() {
  return useQuery({
    queryKey: ["trades", "track-performance"],
    queryFn: () => api.get<{
      math: { total: number; wins: number; avg_return: number; total_pl: number } | null;
      llm: { total: number; wins: number; avg_return: number; total_pl: number } | null;
      legacyLlm: { total: number; wins: number; avg_return: number | null; total_pl: number } | null;
      langgraph: { total: number; wins: number; avg_return: number | null; total_pl: number } | null;
      untagged: { total: number; wins: number; avg_return: number; total_pl: number } | null;
    }>("/api/trades/track-performance"),
  });
}

// Prices
export function usePrices(ticker: string, period = "3M") {
  return useQuery({
    queryKey: ["prices", ticker, period],
    queryFn: () => api.get<ListResponse<PriceBar>>(`/api/prices/${ticker}?period=${period}`),
    enabled: !!ticker,
  });
}

// Backtests
export function useBacktestStrategies() {
  return useQuery({
    queryKey: ["backtests", "strategies"],
    queryFn: () => api.get<ListResponse<BacktestStrategyGroup>>("/api/backtests/strategies"),
  });
}

export function useBacktests(params?: Record<string, string>) {
  const search = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["backtests", params],
    queryFn: () => api.get<PaginatedResponse<BacktestRun>>(`/api/backtests?${search}`),
  });
}

// Market data
export function useCommodities() {
  return useQuery({
    queryKey: ["commodities"],
    queryFn: () => api.get<ListResponse<CommodityCompanyMap>>("/api/commodities"),
  });
}

export function useSectors() {
  return useQuery({
    queryKey: ["sectors"],
    queryFn: () => api.get<ListResponse<SectorRotation>>("/api/sectors"),
  });
}

export function useMacro() {
  return useQuery({
    queryKey: ["macro"],
    queryFn: () => api.get<ListResponse<MacroIndicator>>("/api/macro"),
  });
}

export function useEvents(limit = 20) {
  return useQuery({
    queryKey: ["events", limit],
    queryFn: () => api.get<ListResponse<MarketEvent>>(`/api/events?limit=${limit}`),
  });
}

export function useSupplyDemand() {
  return useQuery({
    queryKey: ["supply-demand"],
    queryFn: () => api.get<ListResponse<SupplyDemand>>("/api/supply-demand"),
  });
}

export function useCorrelations(ticker?: string) {
  const params = ticker ? `?ticker=${ticker}` : "";
  return useQuery({
    queryKey: ["correlations", ticker],
    queryFn: () => api.get<ListResponse<CorrelationSnapshot>>(`/api/correlations${params}`),
  });
}

// Fibs
export function useFibs(ticker: string) {
  return useQuery({
    queryKey: ["fibs", ticker],
    queryFn: () => api.get<{ data: FibTracking | null }>(`/api/fibs/${ticker}`),
    enabled: !!ticker,
  });
}

// Sparklines
export function useSparklines(tickers: string[]) {
  const key = tickers.slice().sort().join(",");
  return useQuery({
    queryKey: ["sparklines", key],
    queryFn: () => api.get<{ data: Record<string, { date: string; close: number }[]> }>(`/api/prices/sparklines?tickers=${encodeURIComponent(tickers.join(","))}`),
    enabled: tickers.length > 0,
    staleTime: 60_000,
  });
}

// Fundamentals
export function useFundamentals(ticker: string) {
  return useQuery({
    queryKey: ["fundamentals", ticker],
    queryFn: () => api.get<{
      fundamentals: CompanyFundamentals;
      analyst: AnalystRecommendation | null;
      earnings: EarningsEvent[];
      ownership: { institutional_pct: number | null; insider_pct: number | null; quarterly_pct_delta: number | null } | null;
      secFilings: { fiscal_period: string; fiscal_year: number; revenue: number; net_income: number }[];
    }>(`/api/fundamentals/${ticker}`),
    enabled: !!ticker,
  });
}

// System
export function useSystemFreshness() {
  return useQuery({
    queryKey: ["system", "freshness"],
    queryFn: () => api.get<ListResponse<FreshnessEntry>>("/api/system/freshness"),
    refetchInterval: 120_000,
  });
}

export function useLangGraphShadow() {
  return useQuery({
    queryKey: ["system", "langgraph-shadow"],
    queryFn: () => api.get<LangGraphShadowResponse>("/api/system/langgraph-shadow"),
    refetchInterval: 120_000,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: () => api.get<HealthResponse>("/api/health"),
    refetchInterval: 60_000,
  });
}

// Brief
export function useBrief() {
  return useQuery({
    queryKey: ["brief"],
    queryFn: () =>
      api.get<{
        date: string;
        signalChanges: SignalChange[];
        topBuys: CompositeSignal[];
        positions: (Trade & { current_price?: number })[];
        macro: MacroIndicator[];
      }>("/api/brief"),
    refetchInterval: 120_000,
  });
}

// Watchlist
export function useWatchlist() {
  return useQuery({
    queryKey: ["watchlist"],
    queryFn: () => api.get<{ data: { id: number; ticker: string; position: number; created_at: string }[] }>("/api/watchlist"),
  });
}

// Signal Replay
export function useSignalReplay(ticker: string, date?: string) {
  const params = date ? `?date=${date}` : "";
  return useQuery({
    queryKey: ["signal-replay", ticker, date],
    queryFn: () => api.get<SignalReplayResponse>(`/api/signals/${ticker}/replay${params}`),
    enabled: !!ticker,
  });
}

// Regime
export function useRegime() {
  return useQuery({
    queryKey: ["regime"],
    queryFn: () => api.get<{ data: RegimeResponse | null }>("/api/regime"),
    refetchInterval: 300_000,
  });
}

export function useRegimeHistory() {
  return useQuery({
    queryKey: ["regime", "history"],
    queryFn: () => api.get<{ data: RegimeResponse[] }>("/api/regime/history"),
  });
}

// Discoveries
export function useDiscoveries(limit = 10) {
  return useQuery({
    queryKey: ["discoveries", limit],
    queryFn: () => api.get<ListResponse<DiscoveryLog>>(`/api/discoveries?limit=${limit}`),
  });
}

// Intelligence
export function useIntelligence(ticker: string) {
  return useQuery({
    queryKey: ["intelligence", ticker],
    queryFn: () => api.get<IntelligenceSummary>(`/api/intelligence/${ticker}`),
    enabled: !!ticker,
  });
}

export function useTargetTracking(ticker: string) {
  return useQuery({
    queryKey: ["targets", ticker],
    queryFn: () => api.get<{ data: TargetTrackingEntry[] }>(`/api/targets/${ticker}`),
    enabled: !!ticker,
  });
}

// Pipeline Health
export function usePipelineHealth() {
  return useQuery({
    queryKey: ["pipeline-health"],
    queryFn: () => api.get<PipelineHealthResponse>("/api/system/pipeline-health"),
    refetchInterval: 300_000,
  });
}

// Commodity Futures
export function useCommodityFutures() {
  return useQuery({
    queryKey: ["commodities", "futures"],
    queryFn: () => api.get<ListResponse<CommodityFutures>>("/api/commodities/futures"),
    refetchInterval: 60_000,
  });
}

// Trade Evaluations
export function useTradeEvaluations(params?: Record<string, string>) {
  const search = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ["trades", "evaluations", params],
    queryFn: () => api.get<PaginatedResponse<TradeEvaluation>>(`/api/trades/evaluations?${search}`),
  });
}

export function useTradeEvalStats() {
  return useQuery({
    queryKey: ["trades", "evaluations", "stats"],
    queryFn: () => api.get<TradeEvalStats>("/api/trades/evaluations/stats"),
  });
}

// Commodity Alerts
export function useAlerts(limit = 15) {
  return useQuery({
    queryKey: ["alerts", limit],
    queryFn: () => api.get<ListResponse<CommodityAlert>>(`/api/alerts/recent?limit=${limit}`),
    refetchInterval: 120_000,
  });
}

// Agent Performance
export function useAgentPerformance() {
  return useQuery({
    queryKey: ["system", "agent-performance"],
    queryFn: () => api.get<ListResponse<AgentPerformance>>("/api/system/agent-performance"),
  });
}

// Pairs Signals
export function usePairs() {
  return useQuery({
    queryKey: ["pairs"],
    queryFn: () => api.get<{ data: PairsSignal[]; date: string | null }>("/api/pairs"),
  });
}

// Research Detail (lazy-loaded per category)
export function useResearchDetail<T>(category: string, ticker: string, enabled = true) {
  return useQuery({
    queryKey: ["research-detail", category, ticker],
    queryFn: () => api.get<PaginatedResponse<T>>(`/api/${category}?ticker=${ticker}&pageSize=10`),
    enabled: enabled && !!ticker,
    staleTime: 60_000,
  });
}

// Intel
export function useIntelArticles(params?: Record<string, string>) {
  const search = params ? new URLSearchParams(params).toString() : "";
  return useQuery({
    queryKey: ["intel", "articles", params],
    queryFn: () => api.get<ListResponse<IntelArticle>>(`/api/intel/articles?${search}`),
    refetchInterval: 60_000,
  });
}

// Futures Curves
export function useFuturesCurves() {
  return useQuery({
    queryKey: ["futures-curves"],
    queryFn: () => api.get<ListResponse<FuturesCurve>>("/api/commodities/curves"),
    refetchInterval: 120_000,
  });
}

// COT Positions
export function useCotPositions() {
  return useQuery({
    queryKey: ["cot"],
    queryFn: () => api.get<ListResponse<CotPosition>>("/api/cot"),
  });
}

export function useTrackingRequests(params?: Record<string, string>) {
  const search = params ? new URLSearchParams(params).toString() : "";
  return useQuery({
    queryKey: ["intel", "tracking", params],
    queryFn: () => api.get<ListResponse<TrackingRequest>>(`/api/intel/tracking?${search}`),
  });
}
